import { Printer, Receipt } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { type PatientDetail, displayPin } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { useCan } from '#client/hooks/useCan';
import { useRecordRecent } from '#client/hooks/useRecordRecent';
import { useAuth } from '#client/providers/AuthProvider';
import { RESOURCE, routes } from '#client/lib/routes';
import { PatientPayments } from './PatientPayments';
import {
  JustRegisteredBanner,
  PatientBillingAndDetails,
  PatientIdentityStrip,
  PatientReportsCard,
} from './PatientRecordSections';
import { PreviousVisitsCard } from './PreviousVisitsCard';
import { SlipDialog } from './SlipDialog';
import { type SlipPrompt, useSlipPrompt } from './useSlipPrompt';
import {
  ShareHistoryCard,
  ShareWhatsAppButton,
  ShareWhatsAppModal,
  type ShareView,
  useShareState,
} from '#client/features/whatsapp';

const PATH = RESOURCE.patients;

/**
 * The patient record.
 *
 * ## Ordering
 *
 * Identity, then reports, then billing, then demographics.
 *
 * The previous order put demographics first, which is backwards for a screen
 * people open mid-workflow: nobody navigates here to read a marital status.
 * They open it because a patient is on the phone, or a sample is in their hand,
 * and what they need is "which reports exist and where are they up to".
 *
 * The identity strip at the top is the line staff read out loud — name, PIN,
 * MR number, age, gender, phone — kept together and always visible rather than scattered
 * across a twelve-item grid.
 */
export function PatientDetailPage() {
  const navigate = useNavigate();
  const can = useCan();
  const slip = useSlipPrompt();

  // One fetch for the button and the card together, so they cannot disagree
  // about the same visit. Delivery status arrives by polling — see the hook.
  const { id } = useParams();
  const share = useShareState(id);
  const [isShareOpen, setIsShareOpen] = useState(false);

  return (
    <ResourceDetailPage<PatientDetail>
      path={PATH}
      noun="patient"
      fullWidth
      editCapability="patient.edit"
      deleteCapability="patient.delete"
      title={(patient) => patient.name}
      deleteLabel={(patient) => `${patient.name} (${patient.pin_no})`}
      subtitle={(patient) => <PatientIdentityStrip patient={patient} />}
      extraActions={(patient) => (
        <>
          {/* A dialog rather than a bare link: the slip prints on three
              papers and in two variants, and the options belong in one place
              instead of being frozen into the href. The links inside it are
              real anchors — see SlipDialog. */}
          <Button variant="secondary" icon={<Receipt />} onClick={slip.openSlip}>
            Print slip
          </Button>
          <Button
            icon={<Printer />}
            onClick={() => void navigate(routes.patientPrint(patient.id))}
            disabled={patient.reports.length === 0}
          >
            Print reports
          </Button>
          {can('whatsapp.share') && (
            <ShareWhatsAppButton
              state={share.state}
              loading={share.loading}
              onOpen={() => setIsShareOpen(true)}
            />
          )}
        </>
      )}
    >
      {(patient, refresh) => (
        <PatientRecord
          patient={patient}
          onChanged={refresh}
          slip={slip}
          share={share}
          shareDialog={{ isOpen: isShareOpen, onClose: () => setIsShareOpen(false) }}
        />
      )}
    </ResourceDetailPage>
  );
}

interface PatientRecordProps {
  patient: PatientDetail;
  onChanged: () => void;
  slip: SlipPrompt;
  share: ShareView;
  shareDialog: { isOpen: boolean; onClose: () => void };
}

function PatientRecord({ patient, onChanged, slip, share, shareDialog }: PatientRecordProps) {
  const navigate = useNavigate();
  const can = useCan();
  const { user } = useAuth();

  useRecordRecent(user?.id, {
    href: routes.patient(patient.id),
    title: patient.name,
    subtitle: displayPin(patient.pin_no),
  });

  return (
    <>
      <SlipDialog
        open={slip.isSlipOpen}
        onClose={slip.closeSlip}
        patientId={patient.id}
        patientName={patient.name}
      />

      {share.state && (
        <ShareWhatsAppModal
          open={shareDialog.isOpen}
          onClose={shareDialog.onClose}
          patientId={patient.id}
          patientName={patient.name}
          state={share.state}
          lastSent={share.shares.find((row) => row.status !== 'failed')}
          onSent={share.refresh}
        />
      )}

      {/* Not a blocking modal on the registration form: the record does
          not exist until the POST returns, the form is unmounting, and
          the operator may legitimately want to do something else first.
          A banner they can ignore is the honest shape. */}
      {slip.isNewlyRegistered && !slip.isSlipOpen && (
        <JustRegisteredBanner
          patient={patient}
          onDismiss={slip.dismissPrompt}
          onPrintSlip={slip.openSlip}
        />
      )}

      {/* Reports first: this is what the screen is opened for. */}
      <PatientReportsCard
        patient={patient}
        canAddTests={can('patient.edit')}
        onAddTests={() => void navigate(routes.patientEdit(patient.id))}
        onOpenReport={(reportId) => void navigate(routes.report(reportId))}
      />

      <PreviousVisitsCard patientId={patient.id} mrNo={patient.mr_no} />

      {can('whatsapp.share') && (
        <ShareHistoryCard patientId={patient.id} shares={share.shares} onRetried={share.refresh} />
      )}

      <PatientBillingAndDetails patient={patient} />

      <PatientPayments patient={patient} onChanged={onChanged} />
    </>
  );
}
